import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";
import yup from "yup";
import { EcheqsMassivesManagementForm } from "@ui-kit/components/index.es";
import Head from "pages/_components/Head";
import ContextMenu from "pages/_components/ContextMenu";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import {
    actions as echeqMassManagementActions,
    selectors as echeqMassManagementSelectors,
} from "reducers/echeqMassManagement";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import * as configUtils from "util/config";
import { download, downloadUrl } from "util/download";
import { formatFileSize } from "util/format";
import * as i18n from "util/i18n";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

import {
    getActionValue,
    mapFormValuesToPreviewPayload,
    mapPreviewPayloadToFormValues,
    resolveDownloadExampleConfigKey,
    resolveUploadParams,
    UPLOAD_ACTIVITY_ID,
    validateUploadActionRequired,
} from "./echeqMassManageUtils";
import { ECHEQS_MASS_MANAGEMENT_LIST_PATH } from "./echeqMassManageConstants";
import { buildDownloadExampleOptions } from "./echeqMassManagePageUtils";
import { canAccessMassEcheqManagementForm, isMassEcheqManagementEnabled } from "./massEcheqManagementFlags";

const FORM_ID = "echeqs.massives.manage.form";

function EcheqsMassManageForm({
    dispatch,
    isDesktop,
    embeddedInV3Shell,
    activeEnvironment,
    accessToken,
    fetchingPreview,
    fetchingPre,
    actionOptions,
    storedFormData,
}) {
    const [formikProps, setFormikProps] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const previousActionRef = useRef(null);
    const preRequestedRef = useRef(false);
    const activeEnvironmentPermissions = activeEnvironment?.permissions;
    const activeEnvironmentId = activeEnvironment?.id;
    const selectedAction = formikProps?.values?.action;
    const setFieldValue = formikProps?.setFieldValue;

    const emptyInitialValues = useMemo(
        () => ({
            action: null,
            reference: "",
            file: null,
        }),
        [],
    );

    const initialValues = useMemo(() => {
        const restored = mapPreviewPayloadToFormValues(storedFormData, actionOptions);
        return restored ?? emptyInitialValues;
    }, [actionOptions, emptyInitialValues, storedFormData]);

    const formInstanceKey = useMemo(() => {
        if (fetchingPre) {
            return "mass-manage-form-loading";
        }
        if (storedFormData?.managementAction) {
            return `mass-manage-form-restore-${storedFormData.managementAction}-${storedFormData.idFile ??
                storedFormData.fileName ??
                "file"}`;
        }
        return `mass-manage-form-new-${actionOptions.length}`;
    }, [actionOptions.length, fetchingPre, storedFormData]);

    const handleGoBack = useCallback(() => {
        dispatch(push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
    }, [dispatch]);

    useEffect(() => {
        if (!isMassEcheqManagementEnabled()) {
            dispatch(push("/desktop"));
            return;
        }

        if (!canAccessMassEcheqManagementForm(activeEnvironmentPermissions)) {
            dispatch(push(ECHEQS_MASS_MANAGEMENT_LIST_PATH));
            return;
        }

        if (!preRequestedRef.current) {
            preRequestedRef.current = true;
            if (actionOptions.length === 0) {
                dispatch(echeqMassManagementActions.preRequest());
            }
        }
    }, [dispatch, activeEnvironmentPermissions, actionOptions.length]);

    useEffect(() => {
        const currentAction = getActionValue(selectedAction);
        if (previousActionRef.current == null && currentAction != null) {
            previousActionRef.current = currentAction;
            return;
        }
        if (
            previousActionRef.current != null &&
            currentAction != null &&
            previousActionRef.current !== currentAction &&
            setFieldValue
        ) {
            setFieldValue("file", null);
        }
        previousActionRef.current = currentAction;
    }, [selectedAction, setFieldValue]);

    const downloadExampleOptions = useMemo(() => buildDownloadExampleOptions(activeEnvironmentPermissions, FORM_ID), [
        activeEnvironmentPermissions,
    ]);

    const handleDownloadExample = useCallback(
        async (actionId) => {
            const downloadUrlConfig = configUtils.get(resolveDownloadExampleConfigKey(actionId));

            if (!downloadUrlConfig) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get(`${FORM_ID}.downloadExample.unavailable`),
                        "warning",
                        ["echeqs"],
                    ),
                );
                return;
            }

            setDownloading(true);
            try {
                downloadUrl(downloadUrlConfig);
            } finally {
                setDownloading(false);
            }
        },
        [dispatch],
    );

    const handleFileProcessFinished = useCallback(
        (_idFile, _message, validLines, invalidLines, totalLines) => {
            if (validLines > 0) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("forms.inputFile.echeq.massive.manage.uploadFile.success", null, {
                            VALID_LINES: validLines,
                            TOTAL_LINES: totalLines,
                        }),
                        invalidLines > 0 ? "warning" : "success",
                        ["echeqs"],
                        null,
                        false,
                    ),
                );
            }

            if (invalidLines > 0 && validLines === 0) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("forms.inputFile.echeq.massive.manage.invalid.file.errorLines"),
                        "warning",
                        ["echeqs"],
                        null,
                        false,
                    ),
                );
            }
        },
        [dispatch],
    );

    const handleFileProcessError = useCallback(
        (error) => {
            if (error?.errorMessage?.includes("File size exceeds maximum allowed size of")) {
                dispatch(
                    notificationActions.showNotification(
                        i18n
                            .get("file.upload.input.labelMaxFileSize")
                            .replace("{filesize}", formatFileSize(configUtils.getInteger("core.maxFileSize"))),
                        "error",
                        ["echeqs"],
                    ),
                );
                return;
            }

            const maxAllowedEcheqs = error?.maxAllowedEcheqs ?? error?.data?.maxAllowedEcheqs;

            if (maxAllowedEcheqs != null && error?.totalLines > maxAllowedEcheqs) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("forms.inputFile.echeq.massive.manage.invalid.file.maxLines", null, {
                            ALLOWED_ECHEQS: maxAllowedEcheqs,
                        }),
                        "error",
                        ["echeqs"],
                        null,
                        false,
                    ),
                );
                return;
            }

            const message =
                (error?.NO_FIELD && i18n.get(error.NO_FIELD)) ||
                error?.data?.NO_FIELD ||
                (error?.errorMessage?.startsWith?.("returnCode.") ? i18n.get(error.errorMessage) : null) ||
                error?.message ||
                i18n.get("forms.inputFile.echeq.massive.manage.invalid.file.type");

            dispatch(notificationActions.showNotification(message, "error", ["echeqs"], null, false));
        },
        [dispatch],
    );

    const handleSubmit = useCallback(
        (values) => {
            dispatch(echeqMassManagementActions.previewRequest(mapFormValuesToPreviewPayload(values)));
        },
        [dispatch],
    );

    const maxFileSize = configUtils.getInteger("core.maxFileSize");
    const hintText = i18n.get(`${FORM_ID}.hint`);

    const downloadErrorFile = useCallback((fileName, base64Data) => {
        download(fileName, base64Data);
    }, []);

    const fileFieldConfig = useMemo(
        () => ({
            filesTypesToAccept: [
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ],
            filesTypeLabel: ["xls", "xlsx"],
            idActivity: UPLOAD_ACTIVITY_ID,
            idForm: "echeqsMassivesManagementForm",
            idFormField: "file",
            accessToken,
            idEnvironment: activeEnvironmentId,
            lang: "es",
            channel: "frontend",
            maxFileSize,
            hasValidateFileType: true,
            hasValidateMaxFileSize: true,
            treatServerWarningAsWarning: true,
            requiresCreditAccount: false,
            downloadTxt: downloadErrorFile,
            resolveUploadParams,
            validateBeforeUpload: validateUploadActionRequired,
            onFileProcessFinished: handleFileProcessFinished,
            onFileProcessError: handleFileProcessError,
        }),
        [
            accessToken,
            activeEnvironmentId,
            downloadErrorFile,
            handleFileProcessError,
            handleFileProcessFinished,
            maxFileSize,
        ],
    );

    const mobileRightContent = useMemo(() => {
        if (isDesktop || downloadExampleOptions.length === 0) {
            return undefined;
        }

        return (
            <ContextMenu
                isDesktop={isDesktop}
                styleContext={{ justifyContent: "flex-end" }}
                color="white"
                mobileTitle={i18n.get(`${FORM_ID}.downloadExample`)}
                mobileIconTitle="SendDown"
                mobileIconTitleVariant="outline"
                mobileTriggerIcon="SendDown"
                mobileTriggerIconVariant="outline"
                items={downloadExampleOptions.map((option) => ({
                    label: option.labelKey,
                    onClick: () => handleDownloadExample(option.id),
                    icon: "None",
                }))}
            />
        );
    }, [downloadExampleOptions, handleDownloadExample, isDesktop]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: `${FORM_ID}.title`,
        goBackPath: ECHEQS_MASS_MANAGEMENT_LIST_PATH,
        scopes: ["echeqs"],
        rightContentMobile: mobileRightContent,
    });

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                action: yup.mixed().required(i18n.get("transfers.validation.required")),
                reference: yup
                    .string()
                    .required(i18n.get("transfers.validation.required"))
                    .max(100, i18n.get(`${FORM_ID}.reference.maxLength`)),
                file: yup
                    .object()
                    .nullable()
                    .required(i18n.get("transfers.validation.required"))
                    .test("valid-lines", i18n.get(`${FORM_ID}.file.invalid`), (value) => Number(value?.validLines) > 0),
            }),
        [],
    );

    const getFormLabel = useCallback((id) => i18n.get(`${FORM_ID}.${id}`), []);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqs" />}
            {!embeddedInV3Shell && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    <Head
                        accessibilityTextId={`${FORM_ID}.title`}
                        title={`${FORM_ID}.title`}
                        titleClassName="header-mobile-title-background-blue white-space-normal align-center"
                        rightContent={() => mobileRightContent}
                        isFetchingExport={downloading}
                        onBack={handleGoBack}
                        headerClassName="blue-main-header-mobile blue-main-title-mobile background-transparent"
                    />
                </div>
            )}
            <EcheqsMassivesManagementForm
                key={formInstanceKey}
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                getFormLabel={getFormLabel}
                loading={fetchingPre}
                loadingSubmit={fetchingPreview}
                actionOptions={actionOptions}
                fileFieldConfig={fileFieldConfig}
                goBack={handleGoBack}
                pageTitle={i18n.get(`${FORM_ID}.title`)}
                hintText={hintText}
                downloadExampleOptions={
                    isDesktop
                        ? downloadExampleOptions.map((option) => ({
                              id: option.id,
                              label: i18n.get(option.labelKey),
                          }))
                        : []
                }
                onDownloadExample={isDesktop && downloadExampleOptions.length > 0 ? handleDownloadExample : undefined}
                downloading={downloading}
                setFormikProps={setFormikProps}
            />
        </>
    );
}

EcheqsMassManageForm.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    embeddedInV3Shell: bool,
    activeEnvironment: shape({
        id: string,
        permissions: shape({}),
    }).isRequired,
    accessToken: string,
    fetchingPreview: bool,
    fetchingPre: bool,
    actionOptions: arrayOf(shape({})),
    storedFormData: shape({}),
};

EcheqsMassManageForm.defaultProps = {
    embeddedInV3Shell: false,
    accessToken: "",
    fetchingPreview: false,
    fetchingPre: false,
    actionOptions: [],
    storedFormData: null,
};

const mapStateToProps = (state) => ({
    activeEnvironment: state.session.activeEnvironment,
    accessToken: sessionSelectors.getAccessToken(state),
    fetchingPreview: echeqMassManagementSelectors.getFetchingPreview(state),
    fetchingPre: echeqMassManagementSelectors.getFetchingPre(state),
    actionOptions: echeqMassManagementSelectors.getActions(state),
    storedFormData: echeqMassManagementSelectors.getFormData(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqsMassManageForm);
